/**
 * Backend → frontend mappers for the Day view read.
 *
 * Translates the C# DTO shapes into the existing frontend types so the UI
 * doesn't notice the swap. Every field is intentionally mapped (no spread)
 * so type drift between backend and frontend produces a compile error
 * rather than a runtime "undefined chip".
 *
 * Scope: Phase 7b (Day view read). Mutation-direction mappers come later.
 */

import type {
  ActivityAttachment,
  CrewColorToken,
  DispatchActivityTask as FrontendDispatchActivityTask,
  DispatchCrew,
  DispatchCrewJobActivity,
  DispatchCrewMember,
  DispatchTaskExecutionStatus,
  DispatchJobStatus,
  DispatchJobType,
  DispatchJobWorker,
  DispatchVehicle,
  UnassignedJob,
  UnassignedKind,
} from '../../types';

import {
  DispatchActivityStatus,
  DispatchTaskStatus,
} from './enums';
import type {
  BackendCrew,
  BackendCrewAsset,
  BackendCrewMember,
  BackendDispatchActivity,
  BackendDispatchActivityAsset,
  BackendDispatchActivityAttachment,
  BackendDispatchActivityTask,
  BackendDispatchActivityWorker,
  BackendDispatchBoardDay,
  BackendUnplannedTask,
} from './types';

/* ───────────────────────────────────────────────────────────────────────── */
/* Small value mappers                                                       */
/* ───────────────────────────────────────────────────────────────────────── */

const VALID_COLOR_TOKENS: ReadonlySet<CrewColorToken> = new Set([
  'sky',
  'emerald',
  'amber',
  'rose',
  'violet',
  'slate',
]);

/** Defensive cast — unknown backend token falls back to 'slate' (visible but neutral). */
const toColorToken = (raw: string | null | undefined): CrewColorToken => {
  if (raw && VALID_COLOR_TOKENS.has(raw as CrewColorToken)) {
    return raw as CrewColorToken;
  }
  return 'slate';
};

/**
 * Map backend's ScaffoldTaskType enum (1..6) to frontend's DispatchJobType
 * string union. MIRRORS the existing legacy mapping in hooks.ts so behavior
 * stays identical:
 *   1 Erect → 'ERECT'
 *   2 Modify → 'MODIFY'
 *   3 Dismantle → 'DISMANTLE'
 *   4 Inspection → 'INSPECT'
 *   5 Maintenance → 'HANDOVER'  (existing UI quirk; cleaning up is out of scope here)
 *   6 General → 'OTHER'
 */
const mapTaskType = (raw: number | undefined | null): DispatchJobType => {
  switch (raw) {
    case 1: return 'ERECT';
    case 2: return 'MODIFY';
    case 3: return 'DISMANTLE';
    case 4: return 'INSPECT';
    case 5: return 'HANDOVER';
    default: return 'OTHER';
  }
};

/**
 * Backend has 3 activity statuses (Scheduled=1, Completed=4, Cancelled=6).
 * Frontend additionally has 'InProgress' but backend never emits it.
 */
const mapActivityStatus = (raw: DispatchActivityStatus): DispatchJobStatus => {
  switch (raw) {
    case DispatchActivityStatus.Scheduled: return 'Scheduled';
    case DispatchActivityStatus.Completed: return 'Completed';
    case DispatchActivityStatus.Cancelled: return 'Cancelled';
    default: return 'Scheduled';
  }
};

/* ───────────────────────────────────────────────────────────────────────── */
/* Crew member / asset                                                       */
/* ───────────────────────────────────────────────────────────────────────── */

const mapCrewMember = (m: BackendCrewMember): DispatchCrewMember => ({
  userId: m.userId,
  name: m.userName?.trim() || 'Unnamed',
  // role isn't on the dispatch member endpoint — back-filled in the hook
  // layer from the staff lookup (see Phase 7b fix). Leave undefined here.
  role: undefined,
  isLead: m.isLead,
  // Phase 7c: preserve the DbCrewMember join-row id so the hook can
  // address `DELETE /Crew/members/{memberId}` against the real backend.
  memberRowId: m.id,
});

/**
 * Crew-level asset (DbCrewAsset) → frontend DispatchVehicle. The crew-asset
 * endpoint doesn't carry assetClass/imageUrl — those come from the tenant
 * asset list (already fetched separately by useDispatchBoardDay) and are
 * back-filled in the hook's mapVehicles step. We leave them undefined here.
 */
const mapCrewAsset = (a: BackendCrewAsset): DispatchVehicle => ({
  assetId: a.assetId,
  // Phase 7c: DbCrewAsset row id — required to call
  // `DELETE /Crew/assets/{assetId}` on the real backend.
  rowId: a.id,
  code: a.assetCode ?? '',
  label: a.assetName ?? 'Asset',
  registration: a.registrationNumber,
  assetClass: undefined,
  imageUrl: undefined,
  // Day-read cartage-captured flag → drives the per-vehicle / crew cartage
  // lock (utils/capture-lock.ts). Computed, not a stored column; defaults
  // false when the read omits it.
  cartageCaptured: a.cartageCaptured ?? false,
  allocations: undefined, // computed in hook layer
});

/* ───────────────────────────────────────────────────────────────────────── */
/* Activity child entities — workers, assets, tasks, attachments             */
/* ───────────────────────────────────────────────────────────────────────── */

const mapWorker = (w: BackendDispatchActivityWorker): DispatchJobWorker => ({
  userId: w.userId,
  name: w.userName?.trim() || 'Unnamed',
  // Phase 7e: preserve the DbDispatchActivityWorker join-row id so the
  // hook can address `DELETE /DispatchActivity/workers/{workerId}`.
  workerRowId: w.id,
  // Rate-band — drives the capture form's "split by role" hours entry.
  roleId: w.roleId ?? undefined,
  roleName: w.roleName ?? undefined,
});

const mapActivityAsset = (a: BackendDispatchActivityAsset): DispatchVehicle => ({
  assetId: a.assetId,
  // Phase 7c: DbDispatchActivityAsset row id — required to call
  // `DELETE /DispatchActivity/assets/{assetId}` on the real backend
  // (used by Phase 7e drag-drop / activity-asset mutations).
  rowId: a.id,
  code: a.assetCode ?? '',
  label: a.assetName ?? 'Asset',
  registration: a.registrationNumber,
  assetClass: undefined,
  imageUrl: undefined,
  // Day-read cartage-captured flag (same source as the crew-asset mapper).
  cartageCaptured: a.cartageCaptured ?? false,
  allocations: undefined,
});

/**
 * Backend DispatchTaskStatus enum (0 Pending / 1 Completed / 2 Partial /
 * 3 NotDone) → FE execution-status string union.
 */
const mapTaskExecStatus = (
  s: DispatchTaskStatus,
): DispatchTaskExecutionStatus => {
  switch (s) {
    case DispatchTaskStatus.Completed: return 'Completed';
    case DispatchTaskStatus.Partial: return 'Partial';
    case DispatchTaskStatus.NotDone: return 'NotDone';
    case DispatchTaskStatus.Cancelled: return 'Cancelled';
    default: return 'Pending';
  }
};

/**
 * Map a backend task row. Some fields (jobNumber/jobName/clientName/siteName)
 * aren't on the task itself — they live on the parent activity. Caller passes
 * them in so the mapped task is fully populated without re-querying.
 */
const mapTask = (
  t: BackendDispatchActivityTask,
  parent: { jobNumber: string; jobName: string; clientName: string; siteName: string },
): FrontendDispatchActivityTask => ({
  taskId: t.id,
  workOrderTaskId: t.workOrderTaskId,
  jobNumber: parent.jobNumber,
  jobName: parent.jobName,
  description: t.workOrderTaskTitle?.trim() || 'Untitled task',
  type: mapTaskType(t.scaffoldTaskType),
  taskTypeRaw: t.scaffoldTaskType,
  scaffoldId: t.scaffoldId ?? undefined,
  scaffoldCode: t.scaffoldNumber,
  scaffoldName: t.scaffoldName,
  // The backend day-read Map now projects WorkOrderId (with a WorkOrderTask
  // fallback for rows whose ScaffoldId wasn't forwarded), so the day-cartage
  // allocation gets a real WorkOrder guid. Undefined only when the task has no WO.
  workOrderId: t.workOrderId ?? undefined,
  workOrderNumber: t.workOrderNumber,
  // The hook's existing actionName derivation is task-type-based; we leave
  // it undefined so the existing UI fallback (uses `type` for the chip) kicks in.
  actionName: undefined,
  clientName: parent.clientName,
  siteName: parent.siteName,
  // Persisted capture outcome — REQUIRED so the roll-over picker (which filters
  // Partial/NotDone) and the re-capture form see the saved status. Previously
  // dropped here, which made roll-over always report "nothing to roll over" on
  // the real API even right after a task was marked Partial.
  executionStatus: mapTaskExecStatus(t.executionStatus),
  perWorkerHours: t.perWorkerHours ?? undefined,
  // Captured per-rate-band hours read back so a re-capture pre-fills the
  // "split by role" entry instead of flattening it to the uniform value.
  roleHours: t.roleHours
    ? t.roleHours.map((r) => ({ roleId: r.roleId, hours: r.hours }))
    : undefined,
});

/**
 * Map a backend attachment to the frontend's ActivityAttachment shape.
 * Backend stores a server URL (filePath); the frontend's shape was designed
 * for client-side base64 dataUrls. The display layer treats both the same:
 * `<img src={dataUrl} />` works equally for HTTPS URLs, and `<a href>` for
 * downloads. The base64-only fields (size, type) become best-effort:
 *   - size = fileSizeBytes
 *   - type = '' (unknown without server hint)
 *   - addedAt = '' (server response doesn't include upload timestamp here)
 *
 * For Phase 7b (Day view) we don't actually render attachments — that's the
 * activity detail (Phase 7d). This mapping exists so the field is populated
 * if/when the read shape carries them.
 */
const mapAttachment = (a: BackendDispatchActivityAttachment): ActivityAttachment => ({
  id: a.id,
  name: a.fileName,
  size: a.fileSizeBytes,
  type: '',
  dataUrl: a.filePath,
  addedAt: '',
});

/* ───────────────────────────────────────────────────────────────────────── */
/* Activity                                                                   */
/* ───────────────────────────────────────────────────────────────────────── */

const mapActivity = (a: BackendDispatchActivity): DispatchCrewJobActivity => {
  const parent = {
    jobNumber: a.jobNumber ?? '',
    jobName: a.jobName ?? '',
    clientName: a.clientName ?? '',
    siteName: a.siteName ?? '',
  };

  const tasks = (a.tasks ?? []).map(t => mapTask(t, parent));

  // The frontend's "type" on an activity is the dominant task type. Derive
  // from the first task when present; default to 'OTHER' for empty activities.
  const derivedType: DispatchJobType = tasks[0]?.type ?? 'OTHER';

  // Unplanned activities have no parent Job. Mirrors mock-store derivation.
  const isUnplanned = !a.jobId;

  return {
    activityId: a.id,
    sourceTaskId: a.sourceWorkOrderTaskId ?? undefined,
    parentJobId: a.jobId ?? undefined,
    parentJobNumber: a.jobNumber ?? undefined,
    parentJobChargeUpType: a.parentJobChargeUpType ?? undefined,
    jobNumber: a.activityNumber ?? '',
    title: a.title,
    type: derivedType,
    status: mapActivityStatus(a.status),
    scheduledStart: a.scheduledStart,
    scheduledEnd: a.scheduledEnd ?? a.scheduledStart,
    position: a.planningOrder ?? 0,
    clientId: a.clientId ?? '',
    clientName: parent.clientName,
    siteId: a.siteId ?? '',
    siteName: parent.siteName,
    siteAddressShort: undefined, // not on response
    siteTypeColorHex: a.siteTypeColorHex ?? undefined,
    notes: a.notes ?? undefined,
    isUnplanned,
    // When unplanned, the dispatcher's free-text description lives in notes
    // on the backend (no separate column). Surface it in both slots so the
    // UI can pick whichever it already uses.
    unplannedDescription: isUnplanned ? (a.notes ?? undefined) : undefined,
    tasks,
    workers: (a.workers ?? [])
      .filter(w => w.isActive) // existing UI doesn't render away/inactive workers
      .map(mapWorker),
    vehicles: (a.assets ?? [])
      .filter(x => x.isActive)
      .map(mapActivityAsset),
    attachments: (a.attachments ?? []).map(mapAttachment),
    // End-of-day capture state — drives the Phase 4 Captured badge + activity
    // lock + staff-freeze, and seeds the re-capture form. The backend day-read
    // already sends these; they were previously dropped here.
    isCaptured: a.isCaptured ?? false,
    capturedAt: a.capturedAt ?? undefined,
    // INVOICED state — the board day-read now projects this (computed from the
    // activity's captured ScaffoldTasks' invoiced labour/cartage rows). Read by
    // `isActivityInvoiced` → drives the INVOICED badge + hard lock. Defaults
    // false when the read omits it (mock mode / non-day reads).
    isLockedByInvoice: a.isLockedByInvoice ?? false,
    travelHours: a.travelHours ?? undefined,
    loadingHours: a.loadingHours ?? undefined,
    standbyHours: a.standbyHours ?? undefined,
  };
};

/* ───────────────────────────────────────────────────────────────────────── */
/* Crew                                                                       */
/* ───────────────────────────────────────────────────────────────────────── */

const mapCrew = (c: BackendCrew): DispatchCrew => ({
  id: c.id,
  name: c.name,
  position: c.position,
  colorToken: toColorToken(c.colorToken),
  notes: undefined, // backend crew has no notes column
  leadUserId: c.leadUserId,
  // Phase 7c: preserve the optimistic-concurrency token. The hook reads
  // it from cache when issuing PUT requests so the backend can detect
  // stale updates. Undefined → backend skips the check (see
  // CrewRepository.Update line 169).
  rowVersion: c.rowVersion ?? undefined,
  members: (c.members ?? []).map(mapCrewMember),
  vehicles: (c.assets ?? []).map(mapCrewAsset),
  jobs: (c.activities ?? []).map(mapActivity),
});

/* ───────────────────────────────────────────────────────────────────────── */
/* Unplanned-tasks rail                                                       */
/* ───────────────────────────────────────────────────────────────────────── */

const mapUnplannedTask = (u: BackendUnplannedTask): UnassignedJob => ({
  kind: 'unplanned-task' as UnassignedKind,
  id: u.id,
  jobId: u.jobId,
  jobNumber: u.jobNumber ?? '',
  jobName: u.jobTitle ?? '',
  title: u.taskDescription?.trim() || u.jobTitle || 'Untitled task',
  type: mapTaskType(u.scaffoldTaskType),
  taskType: u.scaffoldTaskType ?? undefined,
  clientName: u.clientName ?? '',
  siteName: u.siteName ?? '',
  scaffoldId: u.scaffoldId ?? undefined,
  scaffoldCode: u.scaffoldNumber,
  scaffoldName: u.scaffoldName,
  workOrderNumber: u.workOrderNumber,
});

/* ───────────────────────────────────────────────────────────────────────── */
/* Day composite                                                              */
/* ───────────────────────────────────────────────────────────────────────── */

/**
 * Output matches the existing `dispatchApi.getDay` contract PLUS one extra
 * field — `unplannedTasksFromBackend`. The hook layer uses that list directly
 * when present (real-API mode), bypassing `useUnplannedWork` so the rail
 * inherits the backend's correct visibility rule (NOT-on-today AND
 * NEVER-Completed). In mock-store mode the field is omitted and the hook
 * keeps filtering `useUnplannedWork` output by `consumedUnassignedIds` as
 * before — strictly additive.
 */
export interface MappedDay {
  crews: DispatchCrew[];
  standaloneActivities: DispatchCrewJobActivity[];
  consumedUnassignedIds: string[];
  unplannedTasksFromBackend: UnassignedJob[];
}

export const mapBackendDayToFrontend = (day: BackendDispatchBoardDay): MappedDay => {
  const crews = (day.crews ?? []).map(mapCrew);
  const standaloneActivities = (day.standaloneActivities ?? []).map(mapActivity);

  // Derive consumed ids from every task on every activity (crew-pinned +
  // standalone). The backend has already applied its visibility rule, but
  // the rail filter in the hook also uses consumed ids when falling back
  // to useUnplannedWork — populate them so both code paths work.
  const consumed = new Set<string>();
  for (const crew of crews) {
    for (const a of crew.jobs) for (const t of a.tasks) consumed.add(t.workOrderTaskId);
  }
  for (const a of standaloneActivities) {
    for (const t of a.tasks) consumed.add(t.workOrderTaskId);
  }

  return {
    crews,
    standaloneActivities,
    consumedUnassignedIds: Array.from(consumed),
    unplannedTasksFromBackend: (day.unplannedTasks ?? []).map(mapUnplannedTask),
  };
};
